import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from "react-redux";
import {Modal, Space, Button, Tooltip} from "antd";
import {createAvailabilityThunk, deleteAvailabilityThunk, getAvailabilitiesThunk} from "../Feature/AvailabilitySlice";
import {getRoomBookingsThunk} from "../Feature/RoomBookingsSlice";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {CalendarOutlined, ExclamationCircleOutlined, ArrowLeftOutlined, ArrowRightOutlined, QuestionCircleOutlined, CloseOutlined} from "@ant-design/icons";
import Gravatar from "react-gravatar"
import {useParams} from "react-router-dom";
import {Calendar, momentLocalizer} from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarView.css';

dayjs.extend(utc);

moment.locale('en-gb');
const localizer = momentLocalizer(moment);
const {confirm} = Modal;

export const CalendarView = () => {
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [view, setView] = useState('month');
    const [overflowModal, setOverflowModal] = useState({ open: false, date: null, availabilities: [] });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const dispatch = useDispatch();
    const { spaceId } = useParams();

    // Handle window resize for responsive layout
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const user = useSelector((state) => state.firebase.auth);
    const availabilities = useSelector((state) => state.availabilities.availabilities);
    const roomBookings = useSelector((state) => state.roomBookings.roomBookings);

    useEffect(() => {
        dispatch(getAvailabilitiesThunk(spaceId))
        dispatch(getRoomBookingsThunk(spaceId))
    }, [user, dispatch, spaceId]);

    const deleteWithConfirmation = (availability) => {
        confirm({
            title: 'Remove availability?',
            icon: <ExclamationCircleOutlined style={{color: '#ff4d4f'}}/>,
            content: `Are you sure you want to remove your availability for this date?`,
            okText: 'Yes, remove',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                dispatch(deleteAvailabilityThunk({spaceId: spaceId, availabilityId: availability.id})).then(() => {
                    dispatch(getAvailabilitiesThunk(spaceId));
                    // Close modal if it's open and this was the last item
                    if (overflowModal.open && overflowModal.availabilities.length === 1) {
                        setOverflowModal({ open: false, date: null, availabilities: [] });
                    }
                })
            }
        });
    }

    const createWithConfirmation = (date) => {
        const formattedDate = dayjs(date).format('YYYY-MM-DD');
        confirm({
            title: 'Add availability?',
            icon: <CalendarOutlined style={{color: '#6366f1'}}/>,
            content: `Mark yourself as available on ${dayjs(date).format('MMMM D, YYYY')}?`,
            okText: 'Yes, add',
            okType: 'primary',
            cancelText: 'Cancel',
            onOk() {
                dispatch(createAvailabilityThunk({spaceId: spaceId, date: formattedDate})).then(
                    () => dispatch(getAvailabilitiesThunk(spaceId))
                )
            }
        });
    }

    const handleSelectSlot = (slotInfo) => {
        if (!user || !user.email) return;

        const clickedDateStr = dayjs(slotInfo.start).format('YYYY-MM-DD');
        const hasExistingBooking = roomBookings.some(booking => clickedDateStr === dayjs.utc(booking.date).format('YYYY-MM-DD'));
        if (hasExistingBooking) return;

        const hasUserAvailability = availabilities.some(availability => {
            return clickedDateStr === dayjs.utc(availability.date).format('YYYY-MM-DD') &&
                user.email === availability.user.email;
        });

        if (!hasUserAvailability) {
            createWithConfirmation(slotInfo.start);
        }
    }

    // Extracted Badge Renderer for reuse in Modal
    const renderBadge = (availability) => {
        const isCurrentUser = user?.email === availability.user.email;

        const handleBadgeClick = (e) => {
            e.stopPropagation();
            if (isCurrentUser) {
                deleteWithConfirmation({
                    id: availability.id,
                    date: availability.date
                });
            }
        };

        return (
            <div
                key={availability.id}
                className={`rbc-availability-badge ${isCurrentUser ? 'is-current-user' : ''}`}
                onClick={handleBadgeClick}
                style={{ display: 'inline-flex', alignItems: 'center' }}
            >
                <Gravatar size={14} email={availability.user.email} className="rbc-gravatar" />
                <span className="rbc-name">{availability.user.first_name}</span>
                {isCurrentUser && (
                    <div className="rbc-delete-overlay">
                        <CloseOutlined style={{fontSize: '8px'}} />
                    </div>
                )}
            </div>
        );
    };

    // Group and Sort Availabilities
    const groupedAvailabilities = availabilities.reduce((acc, availability) => {
        const dateKey = dayjs.utc(availability.date).format('YYYY-MM-DD');
        if (!acc[dateKey]) {
            acc[dateKey] = {
                id: `group-${dateKey}`,
                start: dayjs.utc(availability.date).toDate(),
                end: dayjs.utc(availability.date).toDate(),
                resource: { type: 'availability-group', availabilities: [] }
            };
        }
        acc[dateKey].resource.availabilities.push(availability);
        return acc;
    }, {});

    const events = Object.values(groupedAvailabilities).map(group => {
        // ALWAYS SORT: Current user first, then alphabetically by first name
        group.resource.availabilities.sort((a, b) => {
            if (user?.email === a.user.email) return -1;
            if (user?.email === b.user.email) return 1;
            return a.user.first_name.localeCompare(b.user.first_name);
        });
        return group;
    });

    const bookingEvents = roomBookings.map(booking => {
        const date = dayjs.utc(booking.date);
        return {
            id: `booking-${booking.id}`,
            title: `Confirmed Booking`,
            start: date.toDate(),
            end: date.toDate(),
            resource: { type: 'booking', booking: booking }
        }
    });

    const allEvents = [...events, ...bookingEvents];

    const EventComponent = ({ event }) => {
        if (event.resource?.type === 'booking') {
            return <div className="rbc-booking-badge">{event.title}</div>
        }

        if (event.resource?.type === 'availability-group') {
            // Responsive: Show only current user on mobile, 3 on desktop (leaving room for 4th)
            const MAX_VISIBLE = isMobile ? 1 : 3;
            const total = event.resource.availabilities.length;
            const visibleAvailabilities = event.resource.availabilities.slice(0, MAX_VISIBLE);
            const hiddenCount = total - MAX_VISIBLE;

            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {visibleAvailabilities.map(availability => renderBadge(availability))}

                    {hiddenCount > 0 && (
                        <div
                            className="rbc-availability-badge more-badge"
                            onClick={(e) => {
                                e.stopPropagation();
                                setOverflowModal({
                                    open: true,
                                    date: event.start,
                                    availabilities: event.resource.availabilities
                                });
                            }}
                        >
                            +{hiddenCount} more
                        </div>
                    )}
                </div>
            );
        }
        return null;
    }

    const MonthEventComponent = ({ event }) => <EventComponent event={event} />

    const DateCellWrapper = ({ children, value }) => {
        const cellDateStr = dayjs(value).format('YYYY-MM-DD');
        const hasBooking = roomBookings.some(booking => cellDateStr === dayjs.utc(booking.date).format('YYYY-MM-DD'));

        return (
            <div className={`rbc-date-cell-wrapper ${hasBooking ? 'has-booking' : ''}`}>
                {children}
            </div>
        );
    };

    const eventStyleGetter = (event) => {
        if (event.resource?.type === 'availability-group') {
            return {
                style: { backgroundColor: 'transparent', border: 'none', padding: 0 }
            };
        }
        return {};
    };

    const handlePrevMonth = () => setSelectedDate(selectedDate.subtract(1, 'month'));
    const handleNextMonth = () => setSelectedDate(selectedDate.add(1, 'month'));
    const handleToday = () => setSelectedDate(dayjs());

    return (
        <Space direction="vertical" style={{width: '100%'}} className="calendar-page">
            <div className="calendar-header">
                <div className="header-title-section" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <h1 className="calendar-title" style={{margin: 0}}>Calendar</h1>
                    <Tooltip title="Click an empty day to add availability. Click your name tag to remove it.">
                        <QuestionCircleOutlined style={{color: '#8c8c8c', cursor: 'pointer'}} />
                    </Tooltip>
                </div>
            </div>

            <div className="calendar-controls" style={{display: 'flex', justifyContent: 'space-between', marginBottom: '16px'}}>
                <div className="calendar-nav" style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={handlePrevMonth} />
                    <span style={{fontSize: '16px', fontWeight: 500, minWidth: '130px', textAlign: 'center'}}>
                        {selectedDate.format('MMMM YYYY')}
                    </span>
                    <Button type="text" icon={<ArrowRightOutlined />} onClick={handleNextMonth} />
                </div>
                <Button onClick={handleToday}>Today</Button>
            </div>

            <div className="calendar-container">
                <Calendar
                    localizer={localizer}
                    events={allEvents}
                    eventPropGetter={eventStyleGetter}
                    startAccessor="start"
                    endAccessor="end"
                    selectable={true}
                    longPressThreshold={10}
                    onSelectSlot={handleSelectSlot}
                    view={view}
                    onView={setView}
                    views={['month']}
                    date={selectedDate.toDate()}
                    onNavigate={(date) => setSelectedDate(dayjs(date))}
                    style={{minHeight: '620px'}}
                    components={{
                        month: { event: MonthEventComponent },
                        dateCellWrapper: DateCellWrapper
                    }}
                />
            </div>

            {/* Overflow Modal */}
            <Modal
                title={`Available on ${dayjs(overflowModal.date).format('MMMM D, YYYY')}`}
                open={overflowModal.open}
                onCancel={() => setOverflowModal({ ...overflowModal, open: false })}
                footer={null}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '16px 0' }}>
                    {overflowModal.availabilities.map(availability => renderBadge(availability))}
                </div>
            </Modal>

            <div className="calendar-footer"></div>
        </Space>
    );
}